import { Button } from "@nimit9/signet-ui/components/button"
import * as Sentry from "@sentry/react"
import type { ErrorInfo, ReactNode } from "react"
import { Component } from "react"

// TanStack Router per-route error component — receives { error, reset } from the router
export function RouteErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center p-8">
      <div className="max-w-md text-center">
        <p className="text-[18px] font-medium tracking-[-0.4px] text-text-primary">
          Something went wrong
        </p>
        <p className="mt-2 text-[13px] text-text-secondary">
          {error.message || "An unexpected error occurred"}
        </p>
        <Button variant="outline" className="mt-6" onClick={reset}>
          Try again
        </Button>
      </div>
    </div>
  )
}

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack)
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center bg-bg-base">
          <div className="max-w-md text-center">
            <p className="text-[18px] font-medium tracking-[-0.4px] text-text-primary">
              Something went wrong
            </p>
            <p className="mt-2 text-[13px] text-text-secondary">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
            <button
              type="button"
              onClick={() => {
                this.setState({ hasError: false, error: null })
                window.location.reload()
              }}
              className="mt-6 rounded-lg bg-accent-green/10 px-5 py-2.5 text-[13px] font-medium text-accent-green transition-colors hover:bg-accent-green/20"
            >
              Reload page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
